import type { Participacao, MinhaSituacao } from '@/types/evento-publico';
import type { EventoVitrine, FiltrosState, IngressoEvento, SeloInscricao } from './types';

export const TIPOS_EVENTO: { valor: string; rotulo: string }[] = [
  { valor: 'acampamento', rotulo: 'Acampamento' },
  { valor: 'retiro', rotulo: 'Retiro' },
  { valor: 'quermesse', rotulo: 'Quermesse' },
  { valor: 'curso', rotulo: 'Curso' },
  { valor: 'encontro', rotulo: 'Encontro' },
  { valor: 'congresso', rotulo: 'Congresso' },
  { valor: 'show', rotulo: 'Show' },
  { valor: 'formacao', rotulo: 'Formação' },
  { valor: 'outro', rotulo: 'Outro' },
];

// Atalhos de categoria da vitrine (ordem pensada para quem procura evento).
export const CATEGORIAS_VITRINE: { valor: string; rotulo: string; icone: string }[] = [
  { valor: 'acampamento', rotulo: 'Acampamentos', icone: 'ri-tent-line' },
  { valor: 'retiro', rotulo: 'Retiros', icone: 'ri-heart-line' },
  { valor: 'quermesse', rotulo: 'Quermesses', icone: 'ri-restaurant-2-line' },
  { valor: 'show', rotulo: 'Shows', icone: 'ri-music-2-line' },
  { valor: 'curso', rotulo: 'Cursos', icone: 'ri-book-open-line' },
  { valor: 'encontro', rotulo: 'Encontros', icone: 'ri-group-line' },
];

export function rotuloTipo(categoria: string | null): string {
  return TIPOS_EVENTO.find((t) => t.valor === categoria)?.rotulo ?? 'Evento';
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

export function formatarDataCurta(inicioIso: string, fimIso: string | null): string {
  const inicio = new Date(inicioIso);
  if (!fimIso) return `${inicio.getDate()} ${MESES[inicio.getMonth()]}`;
  const fim = new Date(fimIso);
  const di = inicio.getDate();
  const mi = MESES[inicio.getMonth()];
  const df = fim.getDate();
  const mf = MESES[fim.getMonth()];
  if (inicio.toDateString() === fim.toDateString()) return `${di} ${mi}`;
  if (inicio.getMonth() === fim.getMonth() && inicio.getFullYear() === fim.getFullYear()) {
    return `${di} – ${df} ${mf}`;
  }
  return `${di} ${mi} – ${df} ${mf}`;
}

export function formatarDia(iso: string): string {
  const d = new Date(iso);
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}`;
}

const MESES_BADGE = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

export function dataBadge(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MESES_BADGE[d.getMonth()]}`;
}

export function formatarMoeda(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Preço só aparece quando há modalidade ABERTA com valor definido:
// - alguma aberta com valor 0 → "Grátis"
// - aberta(s) com valor > 0 → "a partir de R$ X"
// - nenhuma aberta com valor (ex.: inscrições em breve) → sem preço
export function rotuloPreco(participacoes: Participacao[]): string | undefined {
  const abertasComValor = participacoes.filter(
    (p) => p.publica && p.situacao === 'abertas' && p.valor_centavos != null,
  );
  if (abertasComValor.length === 0) return undefined;
  const menor = Math.min(...abertasComValor.map((p) => p.valor_centavos as number));
  if (menor <= 0) return 'Grátis';
  return `a partir de ${formatarMoeda(menor)}`;
}

export function temInscricaoAberta(participacoes: Participacao[]): boolean {
  return participacoes.some((p) => p.publica && p.situacao === 'abertas');
}

// Evento fixado no topo da vitrine (Acampamento Juvenil Sênior).
// O nome vem do banco, então casamos sem acento e sem diferenciar maiúsculas.
export function eEventoFixado(nome: string | null): boolean {
  if (!nome) return false;
  const n = nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return n.includes('juvenil') && n.includes('senior');
}

// Evento com vendas abertas que deve aparecer antes de tudo (Luau).
export function eEventoLuau(nome: string | null): boolean {
  if (!nome) return false;
  const n = nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return n.includes('luau');
}

export function seloInscricoes(
  participacoes: Participacao[],
  minha: MinhaSituacao | undefined,
  slug: string,
  financeiroAtivo: boolean,
  vende_ingressos: boolean,
  ingressos?: IngressoEvento,
  /** true quando o inscrito só pode ver "Inscrição recebida" (sigilo). */
  sigilo?: boolean,
): SeloInscricao | null {
  // Eventos que vendem ingresso: decide só pelos ingressos, nunca por inscrição.
  if (vende_ingressos) {
    const publicas = participacoes.filter((p) => p.publica);
    const comModalidade = publicas.find((p) => p.modalidade_id != null);
    const destinoCompra = comModalidade ? `/ingresso/${comModalidade.modalidade_id}` : null;

    if (ingressos && ingressos.quantidadePaga > 0) {
      return {
        rotulo:
          ingressos.quantidadePaga === 1
            ? 'Você tem 1 ingresso'
            : `Você tem ${ingressos.quantidadePaga} ingressos`,
        cor: 'verde',
        icone: 'visto',
        acao: destinoCompra
          ? {
              rotulo: 'Comprar mais ingressos',
              para: destinoCompra,
              variante: 'primario',
            }
          : {
              rotulo: 'Ver meus ingressos',
              para: '/meus-ingressos',
              variante: 'primario',
            },
        acaoSecundaria: destinoCompra
          ? {
              rotulo: 'Ver meus ingressos',
              para: '/meus-ingressos',
              variante: 'secundario',
            }
          : undefined,
      };
    }
    if (ingressos && ingressos.pedidoAguardandoId != null) {
      return {
        rotulo: 'Ingresso · aguardando pagamento',
        cor: 'ambar',
        icone: 'relogio',
        acao: {
          rotulo: 'Pagar agora',
          para: `/pedido/${ingressos.pedidoAguardandoId}`,
          variante: 'primario',
        },
        acaoSecundaria: destinoCompra
          ? {
              rotulo: 'Comprar mais ingressos',
              para: destinoCompra,
              variante: 'secundario',
            }
          : undefined,
      };
    }
    if (destinoCompra) {
      return {
        rotulo: 'Ingressos à venda',
        cor: 'verde',
        acao: {
          rotulo: 'Comprar ingressos',
          para: destinoCompra,
          variante: 'primario',
        },
      };
    }
    return null;
  }

  // Prioridade 1: já inscrito
  if (minha?.eu_estou_inscrito) {
    const acao = minha.meu_codigo
      ? {
          rotulo: 'Ver minha inscrição',
          para: `/minha-inscricao/${minha.meu_codigo}`,
          variante: 'verde' as const,
        }
      : undefined;
    // Sigilo: nunca revelar confirmada/suplente/aguardando na vitrine.
    if (sigilo) {
      return { rotulo: 'Inscrição recebida', cor: 'cinza', icone: 'visto', acao };
    }
    if (minha.meu_status === 'pendente') {
      return {
        rotulo: 'Aguardando pagamento',
        cor: 'ambar',
        icone: 'relogio',
        acao,
      };
    }
    if (minha.meu_status === 'lista_espera') {
      return { rotulo: 'Você é suplente', cor: 'cinza', acao };
    }
    return { rotulo: 'Você está inscrito', cor: 'verde', icone: 'visto', acao };
  }

  // Prioridade 2: inscreveu outras pessoas
  if (minha && minha.pessoas_que_inscrevi > 0) {
    const n = minha.pessoas_que_inscrevi;
    return {
      rotulo: `Você inscreveu ${n} ${n === 1 ? 'pessoa' : 'pessoas'}`,
      cor: 'roxo',
      acao: { rotulo: 'Ver inscrições', para: '/minhas-inscricoes', variante: 'primario' },
    };
  }

  const publicas = participacoes.filter((p) => p.publica);
  if (publicas.length === 0) return null;

  const situacoes = publicas.map((p) => p.situacao);

  if (situacoes.includes('abertas')) {
    const abertas = publicas.filter((p) => p.situacao === 'abertas');
    const temIlimitada = abertas.some((p) => p.restantes == null);
    const total = abertas.reduce(
      (acc, p) => acc + (typeof p.restantes === 'number' ? p.restantes : 0),
      0,
    );

    const modalidadesAbertas = [...new Set(abertas.map((p) => p.modalidade_id))];
    const destino =
      modalidadesAbertas.length === 1
        ? `${financeiroAtivo ? '/ingresso' : '/inscricao'}/${modalidadesAbertas[0]}`
        : `/e/${slug}`;
    const acao = {
      rotulo: financeiroAtivo ? 'Comprar ingresso' : 'Inscrever-me',
      para: destino,
      variante: 'primario' as const,
    };

    if (!temIlimitada && total <= 20) {
      return { rotulo: 'Últimas vagas', cor: 'laranja', acao };
    }
    return { rotulo: 'Inscrições abertas', cor: 'verde', acao };
  }
  if (situacoes.includes('em_breve')) {
    const emBreve = publicas.filter((p) => p.situacao === 'em_breve');
    const datas = emBreve
      .map((p) => p.inscricoes_abrem_em)
      .filter((d): d is string => !!d)
      .sort();
    const subtexto = datas.length > 0 ? `Abrem em ${formatarDia(datas[0])}` : undefined;
    return { rotulo: 'Inscrições em breve', cor: 'cinza', subtexto };
  }
  if (situacoes.includes('encerradas')) return { rotulo: 'Inscrições encerradas', cor: 'cinza' };
  if (situacoes.includes('esgotado')) return { rotulo: 'Esgotado', cor: 'cinza' };
  return null;
}

export function aplicarFiltros(eventos: EventoVitrine[], filtros: FiltrosState): EventoVitrine[] {
  const agora = new Date();
  const termo = filtros.busca.trim().toLowerCase();

  return eventos.filter((e) => {
    if (termo && !e.nome.toLowerCase().includes(termo)) return false;
    if (filtros.tipo && e.categoria !== filtros.tipo) return false;
    if (filtros.cidade && e.cidade !== filtros.cidade) return false;

    if (filtros.quando !== 'qualquer') {
      const ini = new Date(e.inicio_em);
      if (filtros.quando === 'este_mes') {
        if (ini.getFullYear() !== agora.getFullYear() || ini.getMonth() !== agora.getMonth()) {
          return false;
        }
      } else {
        const limite = new Date(agora.getFullYear(), agora.getMonth() + 3, agora.getDate());
        if (ini < agora || ini > limite) return false;
      }
    }
    return true;
  });
}